import {useLocation} from '@github-ui/react-router'
import type {ReactNode} from 'react'

import {SavedViewConfigurationProvider} from '../../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration} from '../../contexts/views/view-configuration'
import {FieldIdentifier} from './fields'
import {useSavedViewFields} from './use-saved-view-fields'
import {useSavedViewUrlConfiguration} from './use-saved-view-url-configuration'

const TEMP_DEFAULTS: ViewConfiguration = {
  layout: 'list',
  groupBy: FieldIdentifier.LABEL,
  swimlaneBy: null,
  sorts: [],
  visibleFields: [FieldIdentifier.LABEL, FieldIdentifier.ASSIGNEE, FieldIdentifier.MILESTONE],
}

/**
 * Builds the `applied` configuration from what the server actually resolved. Reading it
 * from URL overrides alone would lie whenever the server ignores or falls back on an
 * override (github/views#211): the menu would report what the URL asked for instead of
 * what is on the page. Visibility and sort come from the persisted baseline; legacy
 * payloads without configuration keep their preview defaults and cannot save configuration.
 */
export function viewConfigurationFromPayload(
  payloadConfig: {
    layout: ViewConfiguration['layout']
    groupBy: string | null
    swimlaneBy: string | null
  },
  baseline?: ViewConfiguration,
): ViewConfiguration {
  return {...(baseline ?? TEMP_DEFAULTS), ...payloadConfig}
}

/**
 * The layout a URL that states none inherits.
 *
 * The saved one, and for a payload carrying no saved configuration the same default
 * `useSavedViewUrlActions` is handed below. Both have to read it the same way, or
 * dropping the parameter and deciding what dropping it meant would disagree.
 */
export function baselineLayout(baseline?: ViewConfiguration): ViewConfiguration['layout'] {
  return (baseline ?? TEMP_DEFAULTS).layout
}

/**
 * A saved view's configuration, read from what the server served and written to the URL. Keyed by
 * the page, so another view starts from its own.
 */
export function SavedViewUrlConfiguration({
  applied,
  baseline,
  basePath,
  children,
}: {
  applied: ViewConfiguration
  baseline?: ViewConfiguration
  basePath: string
  children: ReactNode
}) {
  const {pathname} = useLocation()
  const {data: fields} = useSavedViewFields(basePath)
  const urlConfiguration = useSavedViewUrlConfiguration({applied, baseline: baseline ?? TEMP_DEFAULTS, fields})

  return (
    <SavedViewConfigurationProvider
      key={pathname}
      baseline={baseline}
      appliedConfiguration={urlConfiguration.appliedConfiguration}
      onConfigurationChange={urlConfiguration.onConfigurationChange}
      onReset={urlConfiguration.onReset}
    >
      {children}
    </SavedViewConfigurationProvider>
  )
}
